/**
 * Which CPE half an agent is addressed in, and where inside it.
 *
 * The two halves are ROOMS, not arbitrary buckets: on a two-room site CPE A is
 * one room's L2 and CPE B the other's. Putting a node in the wrong half puts
 * its peers on the wrong side of a boundary ARP cannot cross - the node treats
 * a peer in the other room as on-link and never routes to it, while its actual
 * room-mate sits off-subnet and is reachable only by a host route.
 *
 * opsanio-degustator-gun is the worked example. HH11 is room A (a0-001,
 * a0-002) and HH33 is room B (a0-003, a0-004), but the halves were handed out
 * by index parity, so a0-002 drew B and a0-003 drew A: both CROSS-room pairs
 * came out on-link and neither SAME-room pair did, exactly inverted.
 *
 * EVERY AGENT OCCUPIES TWO ADDRESSES - CPE and, right behind it, Redfish. The
 * old parity scheme kept them apart only as a side effect: consecutive agents
 * alternated halves, so within one half the host offsets stepped by two and
 * Redfish landed in the gap. Choose the half by room instead and two room-mates
 * get CONSECUTIVE offsets, so the first one's Redfish lands on the second one's
 * CPE. On degustator-gun that put a0-003's Redfish and a0-004's CPE both on
 * 10.32.3.168.
 *
 * So the slot is counted per half, never globally, and the stride of two is
 * explicit rather than incidental. Numbering each half from its own sequence
 * also reproduces the historical single-room layout exactly - see cpeSlotFor.
 *
 * Lives in its own module, free of anketa-core, so it can be unit-tested - the
 * catalog itself cannot be imported by the test runner.
 */

/** Room A is the first half, room B the second. */
export type CpeHalf = "A" | "B";

/**
 * The half an agent belongs in.
 *
 * @param zonesEnabled two-room site with rooms actually assigned
 * @param zone         the agent's room, "A" unless recorded as "B"
 * @param offset       the agent's 0-based slot
 */
export const cpeHalfFor = (
  zonesEnabled: boolean,
  zone: string,
  offset: number,
): CpeHalf => {
  if (zonesEnabled) {
    return zone === "B" ? "B" : "A";
  }
  // Single-room sites keep the historical allocation. With `offset` running
  // 0,1,2,... the old even/odd split IS `offset % 2`. These are live
  // appliances; renumbering one is an outage.
  return offset % 2 === 0 ? "A" : "B";
};

/**
 * The host offset an agent's CPE address is calculated at, given how many
 * agents already occupy its half.
 *
 * Reserves two addresses per agent (CPE, then Redfish) by stepping the slot by
 * two, and starts half B one address later than half A.
 *
 * That offset-by-one looks arbitrary and is not: it is what makes the
 * single-room case come out unchanged. There the halves alternate, so half A
 * takes the even offsets 0,2,4... and half B the odd 1,3,5... - which is
 * exactly `2 * slot` and `2 * slot + 1`. A single-room site therefore renders
 * byte-for-byte what it always did, while a two-room site simply fills each
 * half from its own sequence.
 *
 * @param half        the half this agent is in
 * @param slotInHalf  how many agents in that half come before it, 0-based
 */
export const cpeSlotFor = (half: CpeHalf, slotInHalf: number): number =>
  slotInHalf * 2 + (half === "B" ? 1 : 0);
